import { format, parseISO } from "date-fns";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "../../../containers/ApiContainer";
import { useHangarsState } from "../../../containers/HangarsStateContainer";
import { useRampsState } from "../../../containers/RampsStateContainer";
import { useSnackbar } from "../../../containers/SnackbarContainer";
import { usePermissions } from "../../../hooks/usePermissions";
import { PlacementService } from "../../../services/PlacementService";
import { TripService } from "../../../services/TripService";
import { CsrStatus, TripWithTenant } from "../../../types";
import { isInfinityDate } from "../../../utils/dateHelpers";
import { Permission } from "../../../utils/permissions";
import { useScheduleSelection } from "./hooks/useScheduleSelection";
import { ScheduleListPresenter } from "./ScheduleListPresenter";

export interface ScheduleListProps {
  trips: TripWithTenant[];
  onTripEdit?: (trip: TripWithTenant) => void;
  loading?: boolean;
  startDate?: Date;
  endDate?: Date;
  onTripsUpdated?: () => void;
  statusFilter?: string;
  tailNumberFilter?: string;
  /** Optimistic update callback - updates local state immediately without API call */
  onCsrStatusChangeOptimistic?: (tripId: string, newStatus: CsrStatus) => void;
}

export const ScheduleList: React.FC<ScheduleListProps> = ({
  trips,
  onTripEdit,
  loading = false,
  startDate,
  endDate,
  onTripsUpdated,
  statusFilter: externalStatusFilter,
  tailNumberFilter: externalTailNumberFilter,
  onCsrStatusChangeOptimistic,
}) => {
  const { t } = useTranslation();
  const { postgrest } = useApi();
  const { notify } = useSnackbar();
  const { hasPermission } = usePermissions();
  const { hangars = [] } = useHangarsState();
  const { ramps = [] } = useRampsState();

  const placementService = useMemo(
    () => new PlacementService(postgrest),
    [postgrest]
  );
  const tripService = useMemo(
    () => new TripService(postgrest),
    [postgrest]
  );

  // Bulk operation state
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  // Use external filters if provided, otherwise manage locally (for backward compatibility)
  const [localStatusFilter, setLocalStatusFilter] = useState<string>("all");
  const [localTailNumberFilter, setLocalTailNumberFilter] =
    useState<string>("");

  const statusFilter =
    externalStatusFilter !== undefined
      ? externalStatusFilter
      : localStatusFilter;
  const tailNumberFilter =
    externalTailNumberFilter !== undefined
      ? externalTailNumberFilter
      : localTailNumberFilter;
  const setStatusFilter =
    externalStatusFilter !== undefined ? () => {} : setLocalStatusFilter;
  const setTailNumberFilter =
    externalTailNumberFilter !== undefined
      ? () => {}
      : setLocalTailNumberFilter;
  const [tripPlacements, setTripPlacements] = useState<Record<string, string>>(
    {}
  );

  // Filter trips by CSR status
  const filteredTrips = useMemo(() => {
    return trips.filter((trip) => {
      const tripCsrStatus = trip.csr_status || "requested";
      const matchesStatus =
        statusFilter === "all" || tripCsrStatus === statusFilter;
      const matchesTailNumber =
        !tailNumberFilter ||
        (trip.tail_number &&
          trip.tail_number
            .toLowerCase()
            .includes(tailNumberFilter.toLowerCase()));

      return matchesStatus && matchesTailNumber;
    });
  }, [trips, statusFilter, tailNumberFilter]);

  // Selection state for bulk operations
  const {
    selectedIds,
    selectedTrips,
    clearSelection,
    setSelectedIds,
  } = useScheduleSelection(filteredTrips);

  // Format date for display (24-hour format)
  const formatDate = (dateString: string) => {
    if (isInfinityDate(dateString)) {
      return "No scheduled departure";
    }
    return format(parseISO(dateString), "MMM d, yyyy HH:mm");
  };

  // Load placement data for all trips
  useEffect(() => {
    const loadTripPlacements = async () => {
      if (!filteredTrips.length) return;

      const placementPromises = filteredTrips.map(async (trip) => {
        try {
          const placements = await placementService.getPlacementsWithLocation(
            trip.trip_id
          );
          const locationNames = placements
            .map((p) => p.location_name)
            .filter((name) => name && name !== "Unassigned");

          return {
            tripId: trip.trip_id,
            assignment:
              locationNames.length > 0
                ? locationNames.join(", ")
                : "Unassigned",
          };
        } catch (error) {
          return { tripId: trip.trip_id, assignment: "Unassigned" };
        }
      });

      const results = await Promise.all(placementPromises);
      const placementMap = results.reduce((acc, { tripId, assignment }) => {
        acc[tripId] = assignment;
        return acc;
      }, {} as Record<string, string>);

      setTripPlacements(placementMap);
    };

    loadTripPlacements();
  }, [filteredTrips, placementService]);

  // Get trip location assignments from cached placement data
  const getTripAssignments = (trip: TripWithTenant): string => {
    return tripPlacements[trip.trip_id] || "Loading...";
  };

  // Handle CSR status change from dropdown
  // Uses optimistic updates to avoid full table refresh
  const handleCsrStatusChange = async (tripId: string, newStatus: CsrStatus) => {
    // Optimistically update UI immediately (no loading state, instant feedback)
    onCsrStatusChangeOptimistic?.(tripId, newStatus);

    try {
      // Persist to database in background
      await tripService.updateTrip(tripId, { csr_status: newStatus });
      // Success - no need to refresh since we already updated optimistically
    } catch (error) {
      console.error("Failed to update trip CSR status:", error);
      // On error, trigger a full refresh to revert to actual database state
      onTripsUpdated?.();
    }
  };

  // Bulk status change handler
  const handleBulkStatusChange = useCallback(
    async (newStatus: CsrStatus) => {
      setIsProcessing(true);
      let successCount = 0;
      let failCount = 0;

      for (const trip of selectedTrips) {
        try {
          await tripService.updateTrip(trip.trip_id, { csr_status: newStatus });
          // Optimistic update for each trip
          onCsrStatusChangeOptimistic?.(trip.trip_id, newStatus);
          successCount++;
        } catch (err) {
          console.error(`Failed to update trip ${trip.trip_id}:`, err);
          failCount++;
        }
      }

      setIsProcessing(false);
      clearSelection();

      if (failCount === 0) {
        notify({
          text: t('screens.schedule.scheduleList.bulkActions.statusSuccess', {
            count: successCount,
          }),
          severity: 'success',
        });
      } else {
        notify({
          text: t('screens.schedule.scheduleList.bulkActions.statusPartial', {
            successCount,
            failCount,
          }),
          severity: 'warning',
        });
        // Refresh to get actual state
        onTripsUpdated?.();
      }
    },
    [selectedTrips, tripService, onCsrStatusChangeOptimistic, clearSelection, notify, t, onTripsUpdated]
  );

  // Bulk delete handler
  const handleBulkDelete = useCallback(() => {
    setShowDeleteConfirmation(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    setIsProcessing(true);
    setShowDeleteConfirmation(false);
    let successCount = 0;

    for (const trip of selectedTrips) {
      try {
        await tripService.deleteTrip(trip.trip_id);
        successCount++;
      } catch (err) {
        console.error(`Failed to delete trip ${trip.trip_id}:`, err);
      }
    }

    setIsProcessing(false);
    clearSelection();
    onTripsUpdated?.();

    notify({
      text: t('screens.schedule.scheduleList.bulkActions.deleteSuccess', {
        count: successCount,
      }),
      severity: 'success',
    });
  }, [selectedTrips, tripService, clearSelection, onTripsUpdated, notify, t]);

  // Bulk cancel handler
  const handleBulkCancel = useCallback(() => {
    setShowCancelConfirmation(true);
  }, []);

  const handleConfirmCancel = useCallback(async () => {
    setIsProcessing(true);
    setShowCancelConfirmation(false);
    let successCount = 0;

    for (const trip of selectedTrips) {
      try {
        await tripService.cancelTrip(trip.trip_id);
        successCount++;
      } catch (err) {
        console.error(`Failed to cancel trip ${trip.trip_id}:`, err);
      }
    }

    setIsProcessing(false);
    clearSelection();
    onTripsUpdated?.();

    notify({
      text: t('screens.schedule.scheduleList.bulkActions.cancelSuccess', {
        count: successCount,
      }),
      severity: 'success',
    });
  }, [selectedTrips, tripService, clearSelection, onTripsUpdated, notify, t]);

  // Bulk location assignment handler
  const handleBulkAssignLocation = useCallback(
    async (locationId: string, locationType: 'hangar' | 'ramp', locationName: string) => {
      setIsProcessing(true);
      let successCount = 0;

      for (const trip of selectedTrips) {
        try {
          await placementService.assignToLocationWithGapFilling(
            trip.trip_id,
            locationId,
            locationType,
            undefined,
            undefined,
            true
          );
          successCount++;
        } catch (err) {
          console.error(`Failed to assign trip ${trip.trip_id}:`, err);
        }
      }

      setIsProcessing(false);
      clearSelection();
      onTripsUpdated?.();

      notify({
        text: t('screens.schedule.scheduleList.bulkActions.assignSuccess', {
          count: successCount,
          locationName,
        }),
        severity: 'success',
      });
    },
    [selectedTrips, placementService, clearSelection, onTripsUpdated, notify, t]
  );

  // Permission check
  const canDelete = hasPermission(Permission.DELETE_TRIPS);

  // Prepare hangars and ramps for the action bar
  const hangarOptions = hangars.map((h) => ({ id: h.id, name: h.name }));
  const rampOptions = ramps.map((r) => ({ id: r.id, name: r.name }));

  return (
    <ScheduleListPresenter
      filteredTrips={filteredTrips}
      trips={trips}
      tripPlacements={tripPlacements}
      statusFilter={statusFilter}
      setStatusFilter={setStatusFilter}
      tailNumberFilter={tailNumberFilter}
      setTailNumberFilter={setTailNumberFilter}
      showFilters={
        externalStatusFilter === undefined &&
        externalTailNumberFilter === undefined
      }
      loading={loading}
      startDate={startDate}
      endDate={endDate}
      onTripEdit={onTripEdit}
      formatDate={formatDate}
      getTripAssignments={getTripAssignments}
      onCsrStatusChange={handleCsrStatusChange}
      // Bulk operation props
      selectedIds={selectedIds}
      setSelectedIds={setSelectedIds}
      selectedTrips={selectedTrips}
      hangars={hangarOptions}
      ramps={rampOptions}
      onBulkStatusChange={handleBulkStatusChange}
      onBulkDelete={handleBulkDelete}
      onBulkCancel={handleBulkCancel}
      onBulkAssignLocation={handleBulkAssignLocation}
      onClearSelection={clearSelection}
      isProcessing={isProcessing}
      canDelete={canDelete}
      // Confirmation dialogs
      showDeleteConfirmation={showDeleteConfirmation}
      setShowDeleteConfirmation={setShowDeleteConfirmation}
      onConfirmDelete={handleConfirmDelete}
      showCancelConfirmation={showCancelConfirmation}
      setShowCancelConfirmation={setShowCancelConfirmation}
      onConfirmCancel={handleConfirmCancel}
    />
  );
};
